import { useEffect, useRef, useState } from "react";
import { loadGoogleMaps } from "./googleMapsLoader.js";
import "./googleMap.css";

/** The exact literal Google Maps deep-link contract the owner specified - never wrapped/shortened/proxied. */
export function googleMapsSearchUrl(latitude, longitude) {
  return `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
}

/** The classic keyless Google Maps embed URL for a single coordinate - real Google-hosted map tiles, no API key/billing required. Owner-approved fallback for when no googleMapsApiKey is configured. */
function keylessEmbedUrl(latitude, longitude) {
  return `https://maps.google.com/maps?q=${latitude},${longitude}&z=15&output=embed`;
}

/**
 * P141R18: a small, real Google Map centered on one real coordinate - the
 * War Room drawer's Tracking/Heartbeat mini map. Uses the real Google Maps
 * JavaScript API (a real google.maps.Map + google.maps.Marker) when
 * `apiKey` is configured; otherwise a real, keyless Google Maps embed
 * <iframe> for this single point (owner-approved - genuine Google-hosted
 * map content, no API key/billing needed, never Leaflet/OpenStreetMap).
 *
 * P141R18 owner correction: no separate visible "เปิดใน Google Maps" link/
 * button - the whole map surface itself is the click target, wrapped in a
 * real <a target="_blank">. Every interaction the underlying map surface
 * would otherwise offer (pan/zoom/drag) is disabled and the surface is
 * `pointer-events: none`, so every click anywhere on it reaches the
 * wrapping anchor - a single, honest "click this to open Google Maps"
 * affordance instead of a competing pannable map plus a separate button.
 */
export default function GoogleMiniMap({ latitude, longitude, apiKey }) {
  const containerRef = useRef(null);
  const [jsApiError, setJsApiError] = useState(false);

  const hasCoordinates = latitude != null && longitude != null;
  const lat = hasCoordinates ? Number(latitude) : null;
  const lng = hasCoordinates ? Number(longitude) : null;
  const hasKey = Boolean(apiKey);

  useEffect(() => {
    if (!hasCoordinates || !hasKey) {
      return undefined;
    }
    let cancelled = false;
    setJsApiError(false);
    loadGoogleMaps(apiKey)
      .then((maps) => {
        if (cancelled || !containerRef.current) {
          return;
        }
        const center = { lat, lng };
        const map = new maps.Map(containerRef.current, {
          center,
          zoom: 15,
          disableDefaultUI: true,
          zoomControl: false,
          draggable: false,
          scrollwheel: false,
          disableDoubleClickZoom: true,
          keyboardShortcuts: false,
          gestureHandling: "none",
          clickableIcons: false,
        });
        // eslint-disable-next-line no-new
        new maps.Marker({ position: center, map });
      })
      .catch(() => {
        if (!cancelled) {
          setJsApiError(true);
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCoordinates, hasKey, apiKey, lat, lng]);

  if (!hasCoordinates) {
    return (
      <div className="wx-gmap-frame wx-gmap-empty">
        <p>ยังไม่มีพิกัดล่าสุด</p>
      </div>
    );
  }

  const searchUrl = googleMapsSearchUrl(lat, lng);
  const accessibleLabel = `เปิดพิกัด ${lat}, ${lng} ใน Google Maps (แท็บใหม่)`;

  return (
    <a
      className="wx-gmap-frame wx-gmap-link-frame"
      href={searchUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={accessibleLabel}
      title={accessibleLabel}
    >
      {hasKey && !jsApiError ? (
        <div ref={containerRef} className="wx-gmap-canvas wx-gmap-canvas--inert" style={{ height: 140 }} data-testid="google-mini-map-canvas" />
      ) : (
        <iframe
          className="wx-gmap-canvas wx-gmap-canvas--inert"
          style={{ height: 140 }}
          src={keylessEmbedUrl(lat, lng)}
          title="ตำแหน่งล่าสุดบน Google Maps"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          tabIndex={-1}
        />
      )}
    </a>
  );
}
